import React, { useState, useRef, useEffect } from "react"

interface CollapsibleProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
}

const Collapsible: React.FC<CollapsibleProps> = ({
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  disabled = false,
  children,
  className = "",
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  
  const open = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  
  const handleOpenChange = (newOpen: boolean) => {
    if (disabled) return;
    
    if (controlledOpen === undefined) {
      setUncontrolledOpen(newOpen);
    }
    
    onOpenChange?.(newOpen);
  };
  
  return (
    <CollapsibleContext.Provider value={{ open, onOpenChange: handleOpenChange, disabled }}>
      <div className={className}>{children}</div>
    </CollapsibleContext.Provider>
  );
};

interface CollapsibleContextType {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled: boolean;
}

const CollapsibleContext = React.createContext<CollapsibleContextType>({
  open: false,
  onOpenChange: () => {},
  disabled: false,
});

const useCollapsibleContext = () => React.useContext(CollapsibleContext);

interface CollapsibleTriggerProps {
  children: React.ReactNode;
  className?: string;
}

const CollapsibleTrigger: React.FC<CollapsibleTriggerProps> = ({
  children,
  className = "",
}) => {
  const { open, onOpenChange, disabled } = useCollapsibleContext();
  
  return (
    <button
      type="button"
      className={className}
      onClick={() => onOpenChange(!open)}
      disabled={disabled}
      aria-expanded={open}
    >
      {children}
    </button>
  );
};

interface CollapsibleContentProps {
  children: React.ReactNode;
  className?: string;
  forceMount?: boolean;
}

const CollapsibleContent = React.forwardRef<
  HTMLDivElement, 
  CollapsibleContentProps
>(({ children, className = "", forceMount = false }, ref) => {
  const { open } = useCollapsibleContext();
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(0);
  const mergedRef = useMergedRef(ref, contentRef);
  
  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(contentHeight);
    }
  }, [children]);
  
  if (!forceMount && !open) {
    return null;
  }
  
  return (
    <div
      ref={mergedRef}
      className={className}
      style={{
        overflow: "hidden",
        height: open ? height : 0,
        opacity: open ? 1 : 0,
        transition: "height 0.2s ease, opacity 0.2s ease",
      }}
    >
      {children}
    </div>
  );
});

CollapsibleContent.displayName = "CollapsibleContent";

// Helper to merge refs
function useMergedRef<T>(
  ...refs: Array<React.RefObject<T> | React.MutableRefObject<T> | ((instance: T | null) => void) | null | undefined>
) {
  return React.useCallback((instance: T | null) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(instance);
      } else if (ref) {
        (ref as React.MutableRefObject<T | null>).current = instance;
      }
    });
  }, [refs]);
}

export {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} 